import { BitmapPatternEditor, Button, Scrollbar, Tooltip, tryDecodePattern } from '@repo/go-ui';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { wrap } from 'src/packages/mobx-di/wrap';

import type { DirectoryRecord } from 'src/services/directory-service/entities/directory-record.entity';
import type { TBitmapImageView } from 'src/services/directory-service/types';

import { normalizeColorToHex } from '../color-picker-component/utils';

import styles from './bitmap-image-component.module.scss';

type Props = {
  record: DirectoryRecord;
  view: TBitmapImageView;
  isEditing?: boolean;
  onChange?(value: string): void;
  onClose?(): void;
};

export const BitmapImageComponent = wrap<Props>(function BitmapImageComponent({
  record,
  view,
  isEditing = false,
  onChange,
  onClose,
}) {
  const { t } = useTranslation();
  const rawValue = record.data[view.fieldId];
  const value = typeof rawValue === 'string' ? rawValue : null;
  const pattern = useMemo(() => (value ? tryDecodePattern(value) : null), [value]);
  const patternColor =
    typeof record.data.patternColor === 'string'
      ? normalizeColorToHex(record.data.patternColor) ?? undefined
      : undefined;
  let backgroundColor: string | undefined;
  if (typeof record.data.backgroundColor === 'string') {
    if (record.data.backgroundColor.trim().toLowerCase() === 'transparent') {
      backgroundColor = 'transparent';
    } else {
      backgroundColor = normalizeColorToHex(record.data.backgroundColor) ?? undefined;
    }
  }
  const [draftValue, setDraftValue] = useState(value);
  const [isOpen, setIsOpen] = useState(isEditing);
  const previewRef = useRef<HTMLDivElement>(null);
  const isInvalid = !!value && !pattern;
  const isEmpty = !pattern?.pixels.includes(1);

  useEffect(() => {
    const preview = previewRef.current;
    if (!preview) {
      return;
    }

    preview.style.backgroundImage = '';
    if (!pattern) {
      return;
    }

    const tile = document.createElement('canvas');
    tile.width = pattern.width;
    tile.height = pattern.height;
    const context = tile.getContext('2d');
    if (!context) {
      return;
    }

    context.fillStyle = backgroundColor ?? 'transparent';
    context.fillRect(0, 0, tile.width, tile.height);
    context.fillStyle = patternColor ?? '#000000';
    for (let index = 0; index < pattern.pixels.length; index++) {
      if (pattern.pixels[index] === 1) {
        context.fillRect(index % pattern.width, Math.floor(index / pattern.width), 1, 1);
      }
    }
    preview.style.backgroundImage = `url(${tile.toDataURL()})`;
  }, [pattern, patternColor, backgroundColor]);

  const closeEditor = () => {
    setIsOpen(false);
    onClose?.();
  };

  const preview = (
    <div
      ref={previewRef}
      className={styles.preview}
      style={{ backgroundColor: backgroundColor ?? 'transparent' }}
      role="img"
      aria-label={t(isInvalid ? 'common:bitmap.invalid' : 'common:bitmap.preview')}
      tabIndex={isEditing ? 0 : -1}
    >
      {(isInvalid || isEmpty) && (
        <span className={styles.placeholder}>{t(isInvalid ? 'common:bitmap.invalid' : 'common:bitmap.empty')}</span>
      )}
    </div>
  );

  if (!isEditing) {
    return preview;
  }

  return (
    <Tooltip
      open={isOpen}
      triggerRef={previewRef}
      trigger={[]}
      role="dialog"
      aria-label={t('common:bitmap.title')}
      placement="bottom-start"
      gap={8}
      withArrow={false}
      className={styles.popover}
      focusManagerProps={{ initialFocus: 0, returnFocus: false }}
      tooltipWrapperProps={{ onKeyDown: (event) => event.stopPropagation() }}
      onOpenChange={(open) => {
        if (!open) {
          closeEditor();
        }
      }}
      title={
        <>
          <h3 className={styles.title}>{t('common:bitmap.title')}</h3>
          <Scrollbar autoHeight noScrollX style={{ maxHeight: 'calc(100dvh - 200px)' }}>
            <div className={styles.editor}>
              <BitmapPatternEditor
                value={draftValue}
                onChange={setDraftValue}
                patternColor={patternColor}
                backgroundColor={backgroundColor}
              />
            </div>
          </Scrollbar>
          <footer className={styles.footer}>
            <span>{t('common:bitmap.applyHint')}</span>
            <Button variant="secondary" onClick={closeEditor}>
              {t('common:cancel')}
            </Button>
            <Button
              disabled={isInvalid || draftValue === value || draftValue === null}
              onClick={() => {
                if (draftValue !== null && draftValue !== value && !isInvalid) {
                  onChange?.(draftValue);
                }
                closeEditor();
              }}
            >
              {t('common:apply')}
            </Button>
          </footer>
        </>
      }
    >
      {preview}
    </Tooltip>
  );
});
